import axios from "axios";
import { API_URL } from "../constants";

export type ProjectActivityType =
  | "CALL"
  | "MEETING"
  | "TASK"
  | "DEADLINE"
  | "EMAIL"
  | "LUNCH";

export type ProjectActivityPriority = "LOW" | "MEDIUM" | "HIGH";

export type ProjectActivity = {
  id: string;
  project: string;
  company: string;
  title: string;
  activityType: ProjectActivityType;
  startAt: number | null;
  endAt: number | null;
  priority: ProjectActivityPriority | null;
  note: string | null;
  location: string | null;
  videoCallUrl: string | null;
  availability: "BUSY" | "FREE";
  assignedTo: { id: string; name: string | null } | null;
  createdBy: { id: string; name: string | null } | null;
  isDone: boolean;
  doneAt: number | null;
  createdAt?: number;
  updatedAt?: number;
};

export type ProjectActivityPayload = {
  title: string;
  activityType: ProjectActivityType;
  startAt?: number | null;
  endAt?: number | null;
  priority?: ProjectActivityPriority | null;
  note?: string | null;
  location?: string | null;
  videoCallUrl?: string | null;
  availability?: "BUSY" | "FREE";
  assignedTo?: string | null;
  isDone?: boolean;
};

export type ScheduledProjectActivity = ProjectActivity & {
  bucket: "overdue" | "today" | "upcoming";
  isOverdue: boolean;
  projectName: string | null;
  /** Deal outcome if closed (WON / LOST / DELETED); null while open. */
  projectOutcome?: string | null;
  boardId: string | null;
  boardName: string | null;
  columnName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  contactName: string | null;
};

export type ScheduledActivitiesResponse = {
  entries: ScheduledProjectActivity[];
  counts: { overdue: number; today: number; upcoming: number };
};

export const fetchProjectActivities = async (
  companyId: string,
  projectId: string
): Promise<ProjectActivity[]> => {
  const url = `${API_URL}/${companyId}/projects/${projectId}/activities`;
  return axios.get(url).then((res) => res.data);
};

export const fetchScheduledProjectActivities = async (
  companyId: string,
  params?: { boardId?: string; assignedTo?: string }
): Promise<ScheduledActivitiesResponse> => {
  const url = `${API_URL}/${companyId}/project-activities`;
  return axios.get(url, { params }).then((res) => res.data);
};

export const createProjectActivity = async (
  companyId: string,
  projectId: string,
  body: ProjectActivityPayload
): Promise<ProjectActivity> => {
  const url = `${API_URL}/${companyId}/projects/${projectId}/activities`;
  return axios.post(url, body).then((res) => res.data);
};

export const patchProjectActivity = async (
  companyId: string,
  projectId: string,
  activityId: string,
  body: Partial<ProjectActivityPayload>
): Promise<ProjectActivity> => {
  const url = `${API_URL}/${companyId}/projects/${projectId}/activities/${activityId}`;
  return axios.patch(url, body).then((res) => res.data);
};

export const deleteProjectActivity = async (
  companyId: string,
  projectId: string,
  activityId: string
): Promise<void> => {
  const url = `${API_URL}/${companyId}/projects/${projectId}/activities/${activityId}`;
  await axios.delete(url);
};
